/* ============================================
   Billing model inference (Fase 1)
   Hasta que `proyectos.modelo_cobro` exista como columna explícita (Fase 2),
   inferimos el modelo desde los datos actuales. Un único punto de verdad
   para el índice de proyectos y el detalle, para que el badge y la vista de
   facturación coincidan siempre.
   ============================================ */

import type { Proyecto, Tarea } from '@/lib/types'

export type ModeloCobro = 'horas' | 'proyecto' | 'tarea'

const MODELOS: readonly ModeloCobro[] = ['horas', 'proyecto', 'tarea']

export const MODELO_LABEL: Record<ModeloCobro, string> = {
  horas: 'Paquete de horas',
  proyecto: 'Por proyecto',
  tarea: 'Por tarea',
}

/**
 * Modelo de cobro del proyecto.
 * Fase 2: se lee la columna explícita `proyecto.modelo_cobro`. La heurística
 * queda solo como fallback defensivo (datos viejos / sin la columna):
 *  - Presupuesto cerrado (> 0)                      → 'proyecto'
 *  - Tareas con cobro de monto fijo / monto > 0     → 'tarea'
 *  - Resto (se trabaja y se registran horas)        → 'horas'
 */
export function inferModeloCobro(proyecto: Proyecto, tareas: Tarea[]): ModeloCobro {
  if (MODELOS.includes(proyecto.modelo_cobro)) return proyecto.modelo_cobro
  if (proyecto.presupuesto && proyecto.presupuesto > 0) return 'proyecto'
  const cobraPorTarea = tareas.some(
    t => t.tipo_cobro === 'monto_fijo' || (t.monto ?? 0) > 0
  )
  if (cobraPorTarea) return 'tarea'
  return 'horas'
}

export interface BillingSummary {
  modelo: ModeloCobro
  /** Cifra principal a mostrar (según modelo) */
  monto: number
  /** Cifra de referencia (presupuesto total / cotizado total) */
  base: number
  /** Horas registradas en el proyecto (para modelo 'horas') */
  horas: number
  moneda: string
}

export function billingSummary(proyecto: Proyecto, tareas: Tarea[]): BillingSummary {
  const modelo = inferModeloCobro(proyecto, tareas)
  const horas = tareas.reduce((s, t) => s + (t.horas_reales ?? 0), 0)
  const cotizado = tareas.reduce((s, t) => s + (t.monto ?? 0), 0)
  const facturado = tareas
    .filter(t => t.factura_id != null)
    .reduce((s, t) => s + (t.monto ?? 0), 0)

  if (modelo === 'proyecto') {
    return { modelo, monto: facturado, base: proyecto.presupuesto ?? 0, horas, moneda: proyecto.moneda }
  }
  if (modelo === 'tarea') {
    return { modelo, monto: facturado, base: cotizado, horas, moneda: proyecto.moneda }
  }
  return { modelo, monto: horas, base: horas, horas, moneda: proyecto.moneda }
}

/** % de avance derivado del estado de las tareas (mismo peso que el Gantt). */
export function progresoProyecto(tareas: Tarea[]): number {
  if (tareas.length === 0) return 0
  const peso: Record<string, number> = { completada: 1, en_progreso: 0.5, bloqueada: 0.1, pendiente: 0 }
  const acc = tareas.reduce((s, t) => s + (peso[t.estado] ?? 0), 0)
  return Math.round((acc / tareas.length) * 100)
}
